import { type ReactNode, useState } from "react";
import SeverityChip, { SeverityGlyph } from "./SeverityChip";
import type { PackSummary } from "./usePackSummaries";
import { repoName } from "../../lib/bobConsole";

export interface RailPack {
  id: string;
  name: string;
  summary: PackSummary | undefined;
}

export interface RailView {
  id: string;
  label: string;
  title: string;
  count: number | null;
}

export interface RailToggle {
  key: string;
  label: string;
  note?: string;
  count: number | null;
  on: boolean;
}

/** Categories past this many fold behind a "more" button. */
const CATEGORY_FOLD = 6;

function Count({ n, label }: { n: number | null | undefined; label?: string }) {
  if (typeof n !== "number") return null;
  return <span className="bob-count">{n}{label && <span className="bob-sr">{` ${label}`}</span>}</span>;
}

function Toggles(
  { legend, items, onToggle, render }: {
    legend: string;
    items: readonly RailToggle[];
    onToggle: (key: string) => void;
    render?: (t: RailToggle) => ReactNode;
  },
) {
  return (
    <fieldset className="bob-rail-set">
      <legend className="bob-eyebrow">{legend}</legend>
      {items.map((t) => (
        <label key={t.key} className="bob-check" data-filter={t.key}>
          <input type="checkbox" checked={t.on} onChange={() => onToggle(t.key)} />
          {render ? render(t) : <span className="bob-check-label">{t.label}</span>}
          <Count n={t.count} />
        </label>
      ))}
    </fieldset>
  );
}

/** Packs, views and filters: the console's left rail. */
export default function PackRail(
  {
    packs, current, onSelectProject, views, view, onView, severities, onSeverity, categories, onCategory, sources, onSource,
  }: {
    packs: readonly RailPack[];
    current: string;
    onSelectProject?: (id: string) => void;
    /** Empty when Bob has not looked at this pack: there is nothing to narrow. */
    views: readonly RailView[];
    view: string;
    onView: (id: string) => void;
    severities: readonly RailToggle[];
    onSeverity: (key: string) => void;
    categories: readonly RailToggle[];
    onCategory: (key: string) => void;
    sources: readonly RailToggle[];
    onSource: (key: string) => void;
  },
) {
  const [allCategories, setAllCategories] = useState(false);
  const shownCategories = allCategories ? categories : categories.slice(0, CATEGORY_FOLD);
  const folded = categories.length - shownCategories.length;

  return (
    <aside className="bob-rail" aria-label="Packs, views and filters">
      {packs.length > 0 && (
        <nav className="bob-rail-nav" aria-label="Packs">
          <span className="bob-eyebrow">Packs</span>
          {packs.map((p) => {
            const on = p.id === current;
            const repo = repoName(p.summary?.repo);
            const where = [repo, p.summary?.branch].filter((s) => typeof s === "string" && s !== "").join(" / ");
            return (
              <button
                key={p.id}
                type="button"
                className={"bob-railbtn" + (on ? " is-on" : "")}
                aria-current={on ? "page" : undefined}
                data-pack={p.id}
                onClick={() => { if (!on) onSelectProject?.(p.id); }}
              >
                <span className="bob-railbtn-main">
                  <span className="bob-railbtn-label">{p.name}</span>
                  {where !== "" && <span className="bob-mono bob-railbtn-sub">{where}</span>}
                </span>
                <span className="bob-railbtn-side">
                  {typeof p.summary?.block === "number" && p.summary.block > 0 && (
                    <span className="bob-chip bob-sev bob-sev-block bob-chip-tiny" title={`${p.summary.block} open BLOCK`}>
                      <SeverityGlyph severity="BLOCK" size={9} />
                      {p.summary.block}
                      <span className="bob-sr"> BLOCK</span>
                    </span>
                  )}
                  <Count n={p.summary?.open} label="open" />
                </span>
              </button>
            );
          })}
        </nav>
      )}

      {views.length > 0 && (
        <nav className="bob-rail-nav" aria-label="Views">
          <span className="bob-eyebrow">Views</span>
          {views.map((v) => (
            <button
              key={v.id}
              type="button"
              className={"bob-railbtn bob-view" + (view === v.id ? " is-on" : "")}
              aria-pressed={view === v.id}
              title={v.title}
              data-view={v.id}
              onClick={() => onView(v.id)}
            >
              <span>{v.label}</span>
              <Count n={v.count} />
            </button>
          ))}
        </nav>
      )}

      {severities.length > 0 && (
        <Toggles
          legend="Severity"
          items={severities}
          onToggle={onSeverity}
          render={(t) => (
            <>
              <SeverityChip severity={t.key} />
              {t.note && <span className="bob-check-note">{t.note}</span>}
            </>
          )}
        />
      )}

      {categories.length > 0 && (
        <fieldset className="bob-rail-set">
          <legend className="bob-eyebrow">Category</legend>
          {shownCategories.map((t) => (
            <label key={t.key} className="bob-check" data-filter={t.key}>
              <input type="checkbox" checked={t.on} onChange={() => onCategory(t.key)} />
              <span className="bob-check-label bob-mono">{t.label}</span>
              <Count n={t.count} />
            </label>
          ))}
          {(folded > 0 || allCategories) && categories.length > CATEGORY_FOLD && (
            <button type="button" className="bob-railbtn bob-rail-more" onClick={() => setAllCategories(!allCategories)}>
              {allCategories ? "Fewer categories" : `${folded} more categor${folded === 1 ? "y" : "ies"}`}
            </button>
          )}
        </fieldset>
      )}

      {sources.length > 0 && <Toggles legend="Source" items={sources} onToggle={onSource} />}
    </aside>
  );
}
